/**
 * RecognitionQuestionsPopup — pops up the first time a logged-in
 * employee lands somewhere with the global layout, if the admin has
 * released any nomination questions that this user hasn't answered.
 *
 * Behaviour:
 *   - Fetches /recognition/questions/active once on mount.
 *   - Shows them one at a time with prev/next and Submit per item.
 *   - sessionStorage flag ("recog-popup-shown") prevents re-fetch +
 *     re-show on every route change in the same browser session.
 *   - Required questions block the Skip; optional ones allow it.
 *   - Closing the dialog (X / Skip all) doesn't mark them answered
 *     server-side; they'll show again next session — that's the
 *     point of "needs to pop up first".
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogActions, IconButton,
  Typography, TextField, Button, Box, Stack, CircularProgress, Avatar,
  Autocomplete, Chip, LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon, EmojiEvents as TrophyIcon,
  ArrowBack, ArrowForward, Send as SendIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import apiClient from '../../services/api/config';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

interface ActiveQuestion {
  id: number;
  prompt: string;
  hint?: string | null;
  questionType: string;
  isRequired: number | boolean;
  releaseAt?: string | null;
  closesAt?: string | null;
  seasonId?: number | null;
  bucketId?: number | null;
  seasonName?: string | null;
  seasonTheme?: string | null;
  seasonImageUrl?: string | null;
  bucketName?: string | null;
  bucketIcon?: string | null;
  bucketColor?: string | null;
}

interface DirectoryUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

const SESSION_KEY = 'recog-popup-shown';

function questionTitle(q: ActiveQuestion): string {
  if (q.seasonName && q.bucketName) return `${q.seasonName} · ${q.bucketName}`;
  if (q.seasonName) return q.seasonName;
  if (q.bucketName) return q.bucketName;
  return 'Recognition';
}

export default function RecognitionQuestionsPopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<ActiveQuestion[]>([]);
  const [idx, setIdx] = useState(0);
  const [directory, setDirectory] = useState<DirectoryUser[]>([]);
  const [nominee, setNominee] = useState<DirectoryUser | null>(null);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadDirectory = useCallback(async () => {
    try {
      const r = await apiClient.get('/chat/users');
      const list = (r.data?.data || []) as DirectoryUser[];
      setDirectory(list);
    } catch (_e) {
      setDirectory([]);
    }
  }, [user]);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/recognition/questions/active');
      const list = (r.data?.data || []) as ActiveQuestion[];
      setQuestions(list);
      if (list.length > 0) {
        setOpen(true);
        loadDirectory();
      }
    } catch (_e) {
      // Silent — non-fatal background fetch.
    } finally {
      setLoading(false);
    }
  }, [loadDirectory]);

  useEffect(() => {
    if (!user) return;
    if ((user as any)?.mustChangePassword) return;
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { /* private mode */ }
    if (alreadyShown) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* private mode */ }
    loadQuestions();
  }, [user, loadQuestions]);

  // Reset per-question form when the index changes.
  useEffect(() => {
    setNominee(null);
    setResponse('');
  }, [idx]);

  if (!open || questions.length === 0) return null;
  const q = questions[idx];
  const total = questions.length;
  const progress = ((idx + 1) / total) * 100;
  const isNominate = q.questionType === 'nominate_colleague';

  const submitOne = async () => {
    if (isNominate && !nominee) { toast.error('Pick a colleague to nominate'); return; }
    if (!isNominate && !response.trim()) { toast.error('Please write a response'); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/recognition/questions/${q.id}/respond`, {
        nomineeId: nominee?.id || null,
        response: response.trim() || (nominee ? `Nominated ${nominee.firstName} ${nominee.lastName}` : null),
      });
      toast.success('Thanks for sharing');
      // Drop this question out of the deck and advance.
      const next = questions.filter((_, i) => i !== idx);
      setQuestions(next);
      if (next.length === 0) {
        setOpen(false);
      } else {
        setIdx((i) => Math.min(i, next.length - 1));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Could not save your answer';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const closeIfAllowed = () => {
    // The toggle is "needs to pop up first" — admins can mark a
    // question required, in which case the user shouldn't be able to
    // dismiss until they answer. Optional questions allow skipping.
    if (q.isRequired) {
      toast('This question is required — please answer to continue.', { icon: 'ℹ️' });
      return;
    }
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={closeIfAllowed}
      maxWidth="sm"
      fullWidth
      // We don't want a stray click outside to skip a required
      // question — Material's Dialog already calls onClose for that
      // and the gate above blocks it.
      disableEscapeKeyDown={!!q.isRequired}
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          background: q.seasonImageUrl
            ? `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.55)), url(${resolveAssetUrl(q.seasonImageUrl)}) center/cover`
            : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          color: 'white',
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{
            bgcolor: q.bucketColor || 'rgba(255,255,255,0.25)',
            border: '2px solid rgba(255,255,255,0.35)',
            width: 44, height: 44, fontSize: '1.3rem',
          }}>
            {q.bucketIcon || <TrophyIcon />}
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 700 }}>
              {questionTitle(q)}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Question {idx + 1} of {total}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={closeIfAllowed} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <LinearProgress variant="determinate" value={progress} sx={{ height: 4 }} />
      <DialogContent dividers>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {q.prompt}
          {q.isRequired ? <Chip size="small" label="Required" color="warning" sx={{ ml: 1 }} /> : null}
        </Typography>
        {q.hint && (
          <Typography variant="body2" sx={{ color: 'var(--color-neutral-500)', mb: 2 }}>{q.hint}</Typography>
        )}
        {isNominate && (
          <Autocomplete
            sx={{ mt: 1, mb: 2 }}
            options={directory}
            value={nominee}
            onChange={(_, v) => setNominee(v)}
            getOptionLabel={(o) => `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Nominate a colleague" placeholder="Type a name…" size="small" />
            )}
          />
        )}
        <TextField
          fullWidth
          multiline
          minRows={isNominate ? 2 : 4}
          label={isNominate ? 'Why? (optional)' : 'Your answer'}
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          placeholder={isNominate ? 'A sentence on why they deserve it…' : 'Share your answer…'}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box>
          <Button
            startIcon={<ArrowBack />}
            disabled={idx === 0 || submitting}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
          >
            Back
          </Button>
          <Button
            startIcon={<ArrowForward />}
            disabled={idx >= total - 1 || submitting}
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
          >
            Next
          </Button>
        </Box>
        <Stack direction="row" spacing={1}>
          {!q.isRequired && (
            <Button onClick={closeIfAllowed} disabled={submitting}>Skip all</Button>
          )}
          <Button
            variant="contained"
            onClick={submitOne}
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={submitting}
            sx={{
              bgcolor: '#6366F1', color: 'white',
              '&:hover': { bgcolor: '#4F46E5' },
              fontWeight: 700, textTransform: 'none',
            }}
          >
            {submitting ? 'Sending…' : 'Submit answer'}
          </Button>
        </Stack>
      </DialogActions>
      {loading && <LinearProgress />}
    </Dialog>
  );
}
